import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  useHandlers,
  useUser,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useSyncStatus } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-sync-log-status';
import { useTriggerManualSync } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-trigger-manual-sync';
import type { SyncLogIdentifier } from '@sparklayer-io/spark-dash-pages/features/sync/types';
import { Heading, LearnMoreLink, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { CustomerLookupSearch } from '../components/customer-lookup-search';
import { CustomersSyncLogAlert } from '../components/customers-sync-log-alert';
import { CUSTOMERS_SYNC_PATH, DOCS_URLS, I18N_PAGE } from '../constants';

type CustomersSyncPageProps = {
  /** Pre-fills and runs the lookup on mount, so a shared `?id=` link jumps straight to a result. */
  initialSearchTerm?: string;
};

export const CustomersSyncPage = withPageProviders(
  ({ initialSearchTerm }: CustomersSyncPageProps) => {
    const env = useEnv();
    const { isSparkStaff } = useUser();
    const { navigationHandler } = useHandlers();

    const syncFor: SyncLogIdentifier = { integration: env.platform ?? '', dataType: 'customers' };
    // Same query key as CustomersSyncLogAlert, so refetching here updates the banner it renders.
    const { refetch: refetchSyncStatus, isFetching: isRefetchingStatus } = useSyncStatus(syncFor);

    const { triggerManualSync, isSyncing } = useTriggerManualSync({
      syncFor,
      successMessage: t(`${I18N_PAGE}.notifications.sync_started`),
    });

    return (
      <Page
        title={t(`${I18N_PAGE}.title`)}
        description={t(`${I18N_PAGE}.description`)}
        learnMoreUrl={DOCS_URLS.dataSync}
        primaryAction={
          isSparkStaff
            ? {
                label: t(`${I18N_PAGE}.actions.manual_sync`),
                onClick: triggerManualSync,
                loading: isSyncing,
              }
            : undefined
        }
        secondaryActions={[
          {
            label: t('buttons:reload'),
            onClick: () => void refetchSyncStatus(),
            loading: isRefetchingStatus,
          },
        ]}
      >
        <CustomersSyncLogAlert />

        <div className="flex flex-col gap-1">
          <Heading style="md" level="h2">
            {t(`${I18N_PAGE}.lookup_title`)}
          </Heading>
          <Text variant="secondary">
            {t(`${I18N_PAGE}.lookup_description`)} <LearnMoreLink link={DOCS_URLS.dataSyncLookup} />
          </Text>
        </div>

        <CustomerLookupSearch
          initialSearchTerm={initialSearchTerm}
          onSelect={(_customer, identifier) =>
            navigationHandler.navigate(`${CUSTOMERS_SYNC_PATH}/${encodeURIComponent(identifier)}`, {
              newTab: true,
            })
          }
        />
      </Page>
    );
  },
);
